import { useLocation, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Package, Watch, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OrderConfirmation = () => {
  const location = useLocation();
  const order = location.state?.order;

  if (!order) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="order-confirmation-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/80 backdrop-blur-md">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Watch className="w-8 h-8 text-gold-500" />
              <span className="font-serif text-2xl text-white tracking-tight">CHRONOS</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full text-center space-y-8"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 bg-gold-500 mx-auto flex items-center justify-center"
          >
            <Check className="w-12 h-12 text-black" />
          </motion.div>

          {/* Message */}
          <div className="space-y-4">
            <h1 className="font-serif text-4xl text-white">Thank You</h1>
            <p className="text-white/60">
              Your order has been confirmed. A confirmation email has been sent to{' '}
              <span className="text-gold-500">{order.customer_info?.email}</span>
            </p>
          </div>

          {/* Order Details */}
          <div className="bg-charcoal border border-white/5 p-6 text-left space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-xs uppercase tracking-wider text-gold-500">Order Number</span>
              <span className="text-white font-mono" data-testid="order-id">{order.id?.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs uppercase tracking-wider text-gold-500">Total</span>
              <span className="font-serif text-xl text-white" data-testid="order-total">
                ${order.total?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs uppercase tracking-wider text-gold-500">Items</span>
              <span className="text-white">{order.items?.length} timepiece(s)</span>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="bg-white/5 p-6 flex items-start gap-4 text-left">
            <Package className="w-6 h-6 text-gold-500 flex-shrink-0 mt-1" />
            <div>
              <p className="text-white font-medium mb-1">Estimated Delivery</p>
              <p className="text-white/50 text-sm">
                Your timepiece will arrive within 5-7 business days in our signature presentation box with certificate of authenticity.
              </p>
            </div>
          </div>

          {/* CTA */}
          <Link to="/">
            <Button className="w-full bg-gold-500 text-black hover:bg-gold-400 rounded-none py-6 text-sm uppercase tracking-widest font-bold btn-glow" data-testid="continue-shopping-btn">
              Continue Shopping
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>

          {/* Support */}
          <p className="text-white/30 text-xs">
            Questions? Contact our concierge at concierge@chronos.ch
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
